import { cubeLoad } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";
import _ from "lodash";
import dateUtils from "utils/dateUtils";
import mathUtils from "utils/mathUtils";

import { selectReferenceDate } from "modules/customer/insights/performance/performanceSlice";

import actions from "./actions";

const getTotalSalesGrowthByRegion = () => async (dispatch, getState) => {
    dispatch(actions.getTotalSalesGrowthByRegionRequest());
    try {
        const state = getState();
        const referenceDate = selectReferenceDate(state);
        const priorTwelveMonthsStartDate = dateUtils.priorTwelveMonthsStartDate(referenceDate);
        const twelveMonthsBeforePriorTwelveMonthsStartDate = dateUtils.twelveMonthsBeforePriorTwelveMonthsStartDate(referenceDate);
        const twelveMonthsBeforePriorTwelveMonthsEndDate = dateUtils.twelveMonthsBeforePriorTwelveMonthsEndDate(referenceDate);
        const query = {
            measures: ["F_Sales.SumLineValue"],
            timeDimensions: [{
                dimension: "D_Date.Date",
                compareDateRange: [
                    [priorTwelveMonthsStartDate, referenceDate],
                    [twelveMonthsBeforePriorTwelveMonthsStartDate, twelveMonthsBeforePriorTwelveMonthsEndDate]
                ]
            }],
            dimensions: ["D_Store.k_Region", "D_Store.RegionCode"],
            filters: [{
                member: "D_Store.OnlineFlag",
                operator: "equals",
                values: ["0"]
            }]
        }; //filters to just physical stores
        const resultSet = await dispatch(cubeLoad(query));
        const priorTwelveMonths = resultSet.loadResponses[0].data.map(item => ({
            regionId: item["D_Store.RegionCode"],
            regionName: item["D_Store.k_Region"],
            sales: Number(item["F_Sales.SumLineValue"])
        }));
        const twelveMonthsBeforePriorTwelveMonths = resultSet.loadResponses[1].data.map(item => ({
            regionId: item["D_Store.RegionCode"],
            regionName: item["D_Store.k_Region"],
            sales: Number(item["F_Sales.SumLineValue"])
        }));

        const growthValues = [];
        const regionsGrowth = priorTwelveMonths.map(current => {
            const previous = twelveMonthsBeforePriorTwelveMonths.find(item => item.regionId === current.regionId);
            let growth = 0;
            if (previous && previous.sales !== 0) {
                growth = ((current.sales - previous.sales) / previous.sales) * 100;
            }

            growthValues.push(growth);
            return {
                ...current,
                growth
            };
        });

        const percentileThresholds = mathUtils.percentileThresholds(growthValues, 6);
        const regionsGrowthWithGroup = regionsGrowth.map(region => {
            let group = 0;
            for (let i = 0; i < percentileThresholds.length; i++) {
                if (region.growth >= percentileThresholds[i]) {
                    group = i;
                }
            }
            return {
                ...region,
                group
            };
        });

        dispatch(actions.getTotalSalesGrowthByRegionSuccess(regionsGrowthWithGroup));
    }
    catch (error) {
        dispatch(actions.getTotalSalesGrowthByRegionFailure());
        dispatch(logError("Error loading TotalSalesGrowthByRegion.", error));
    }
};

const getSalesByRegionGrowth = () => async (dispatch, getState) => {
    dispatch(actions.getSalesByRegionGrowthRequest());
    try {
        const state = getState();
        const referenceDate = selectReferenceDate(state);
        const priorTwelveMonthsStartDate = dateUtils.priorTwelveMonthsStartDate(referenceDate);
        const twelveMonthsBeforePriorTwelveMonthsStartDate = dateUtils.twelveMonthsBeforePriorTwelveMonthsStartDate(referenceDate);
        const twelveMonthsBeforePriorTwelveMonthsEndDate = dateUtils.twelveMonthsBeforePriorTwelveMonthsEndDate(referenceDate);
        const query = {
            measures: ["F_Sales.SumLineValue"],
            timeDimensions: [{
                dimension: "D_Date.Date",
                compareDateRange: [
                    [priorTwelveMonthsStartDate, referenceDate],
                    [twelveMonthsBeforePriorTwelveMonthsStartDate, twelveMonthsBeforePriorTwelveMonthsEndDate]
                ]
            }],
            dimensions: ["D_Store.k_Region", "D_Store.StoreName", "D_Store.RegionCode", "D_Store.StoreNaturalID"],
            filters: [{
                member: "D_Store.OnlineFlag",
                operator: "equals",
                values: ["0"]
            }, {
                member: "F_Sales.SumLineValue",
                operator: "gt",
                values: ["0"]
            }]
        }; //filters to just physical stores, stores with sales (open stores)
        const resultSet = await dispatch(cubeLoad(query));
        const priorTwelveMonths = resultSet.loadResponses[0].data.map(item => ({
            storeId: item["D_Store.StoreNaturalID"],
            regionId: item["D_Store.RegionCode"],
            regionName: item["D_Store.k_Region"],
            sales: Number(item["F_Sales.SumLineValue"]),
            storeName: item["D_Store.StoreName"] ?? item["D_Store.StoreNaturalID"]
        }));
        const twelveMonthsBeforePriorTwelveMonths = resultSet.loadResponses[1].data.map(item => ({
            storeId: item["D_Store.StoreNaturalID"],
            regionId: item["D_Store.RegionCode"],
            regionName: item["D_Store.k_Region"],
            sales: Number(item["F_Sales.SumLineValue"]),
            storeName: item["D_Store.StoreName"] ?? item["D_Store.StoreNaturalID"]
        }));

        //Only stores that had sales in both years
        const priorTwelveMonthsLikeForLikeStores = _.intersectionBy(priorTwelveMonths, twelveMonthsBeforePriorTwelveMonths, "storeId");
        const twelveMonthsBeforePriorTwelveMonthsLikeForLikeStores = _.intersectionBy(twelveMonthsBeforePriorTwelveMonths, priorTwelveMonths, "storeId");

        const storesGrowth = priorTwelveMonthsLikeForLikeStores.map(current => {
            const previous = twelveMonthsBeforePriorTwelveMonthsLikeForLikeStores.find(item => item.storeId === current.storeId);
            let growth = 0;
            if (previous && previous.sales !== 0) {
                growth = ((current.sales - previous.sales) / previous.sales) * 100;
            }

            return {
                ...current,
                growth
            };
        });

        dispatch(actions.getSalesByRegionGrowthSuccess(storesGrowth));
    }
    catch (error) {
        dispatch(actions.getSalesByRegionGrowthFailure());
        dispatch(logError("Error loading SalesByRegionGrowth.", error));
    }
};

const getDependencyOnSingularRegionOrStore = () => async (dispatch, getState) => {
    dispatch(actions.getDependencyOnSingularRegionOrStoreRequest());
    try {
        const state = getState();
        const referenceDate = selectReferenceDate(state);
        const priorTwelveMonthsStartDate = dateUtils.priorTwelveMonthsStartDate(referenceDate);
        const twelveMonthsBeforePriorTwelveMonthsStartDate = dateUtils.twelveMonthsBeforePriorTwelveMonthsStartDate(referenceDate);
        const twelveMonthsBeforePriorTwelveMonthsEndDate = dateUtils.twelveMonthsBeforePriorTwelveMonthsEndDate(referenceDate);
        const query = {
            measures: ["F_Sales.SumLineValue"],
            timeDimensions: [{
                dimension: "D_Date.Date",
                compareDateRange: [
                    [priorTwelveMonthsStartDate, referenceDate],
                    [twelveMonthsBeforePriorTwelveMonthsStartDate, twelveMonthsBeforePriorTwelveMonthsEndDate]
                ]
            }],
            dimensions: ["D_Store.k_Region", "D_Store.RegionCode", "D_Store.StoreNaturalID", "D_Store.OpeningDate"],
            filters: [{
                member: "D_Store.OnlineFlag",
                operator: "equals",
                values: ["0"]
            }, {
                member: "F_Sales.SumLineValue",
                operator: "gt",
                values: ["0"]
            }, {
                member: "D_Store.ClosingDate",
                operator: "notSet"
            }]
        }; //filters to just physical stores, stores with sales (open stores)
        const resultSet = await dispatch(cubeLoad(query));
        const priorTwelveMonths = resultSet.loadResponses[0].data.filter(i => Date.parse(i["D_Store.OpeningDate"]) <= twelveMonthsBeforePriorTwelveMonthsStartDate).map(item => ({
            storeId: item["D_Store.StoreNaturalID"],
            regionId: item["D_Store.RegionCode"],
            regionName: item["D_Store.k_Region"],
            sales: Number(item["F_Sales.SumLineValue"])
        }));
        const twelveMonthsBeforePriorTwelveMonths = resultSet.loadResponses[1].data.filter(i => Date.parse(i["D_Store.OpeningDate"]) <= twelveMonthsBeforePriorTwelveMonthsStartDate).map(item => ({
            storeId: item["D_Store.StoreNaturalID"],
            regionId: item["D_Store.RegionCode"],
            regionName: item["D_Store.k_Region"],
            sales: Number(item["F_Sales.SumLineValue"])
        }));

        //Only stores that had sales in both years
        const priorTwelveMonthsLikeForLikeStores = _.intersectionBy(priorTwelveMonths, twelveMonthsBeforePriorTwelveMonths, "storeId");
        const twelveMonthsBeforePriorTwelveMonthsLikeForLikeStores = _.intersectionBy(twelveMonthsBeforePriorTwelveMonths, priorTwelveMonths, "storeId");

        const storesGrowth = priorTwelveMonthsLikeForLikeStores.map(current => {
            const previous = twelveMonthsBeforePriorTwelveMonthsLikeForLikeStores.find(item => item.storeId === current.storeId);
            let growth = 0;
            let rawGrowth = 0;
            let previousSales = 0;
            if (previous && previous.sales !== 0) {
                previousSales = previous.sales;
                growth = ((current.sales - previous.sales) / previous.sales) * 100;
                rawGrowth = (current.sales) - (previous.sales);
            }

            return {
                ...current,
                previousSales,
                growth,
                rawGrowth
            };
        });

        //Get distinct region IDs:
        const allRegions = storesGrowth.map(function (el) { return el.regionId; });

        const currentDistinctRegions = [...new Set(allRegions)];

        const regionSales = [];
        for (let i = 0; i < currentDistinctRegions.length; i++) {
            let currentRegionId = currentDistinctRegions[i];
            let currentRegionSales = 0;
            let previousRegionSales = 0;
            let regionGrowth = 0;
            for (let j = 0; j < storesGrowth.length; j++) {
                if (storesGrowth[j].regionId === currentRegionId) {
                    currentRegionSales = currentRegionSales + storesGrowth[j].sales;
                    previousRegionSales = previousRegionSales + storesGrowth[j].previousSales;
                    regionGrowth = (currentRegionSales) - (previousRegionSales);
                }
            }
            regionSales.push({ regionId: currentRegionId, currentRegionSales: currentRegionSales, previousRegionSales: previousRegionSales, regionGrowth: regionGrowth });
        }

        const distinctRegionSales = regionSales;

        const storeNamesQuery = {
            dimensions: [
                "D_Store.StoreNaturalID",
                "D_Store.StoreName"
            ],
            filters: [{
                member: "D_Store.OnlineFlag",
                operator: "equals",
                values: ["0"]
            }]
        }; //filters to just physical stores
        const storeNamesResultSet = await dispatch(cubeLoad(storeNamesQuery));
        const stores = storeNamesResultSet.loadResponses[0].data.map(item => ({
            storeId: item["D_Store.StoreNaturalID"],
            storeName: item["D_Store.StoreName"]
        }));

        const storesGrowthWithStoreName = storesGrowth.map(storeGrowth => {
            const storeName = stores.find(store => store.storeId === storeGrowth.storeId)?.storeName ?? storeGrowth.storeId;
            return {
                ...storeGrowth,
                storeName
            };
        });

        let ragLabel;
        let ragStatus;
        let ragValue;
        let y;

        //Prior 12 months sales:
        const priorTweleveMonthsSales = priorTwelveMonths.reduce((total, item) => item.sales + total, 0);

        //12 months prior sales:
        const tweleveMonthBeforePriorTweleveMonthsSales = twelveMonthsBeforePriorTwelveMonths.reduce((total, item) => item.sales + total, 0);

        //Overall company YoY Sales increase (Last 12 months sales) - (12 months prior to last 12 months sales):
        const overallCompanyYoYSales = (priorTweleveMonthsSales) - (tweleveMonthBeforePriorTweleveMonthsSales);

        //Get number of distinct regions
        const numberDistinctRegions = currentDistinctRegions.length;

        //Check the number of regions:
        if (numberDistinctRegions === 1) { //only one region
            //check for dependency on singular store

            //Set label value:
            ragLabel = "Dependency on singular store";

            //Get each stores YoY Sales Growth:
            const storeGrowth = storesGrowthWithStoreName.filter(storeSales => storeSales.growth);
            //Get number of stores:
            const numberOfStores = storeGrowth.length;

            //No Dependency on a single store for growth:
            let checkStoreNoDependency = 0;
            //Some Dependency on a single store for growth:
            let checkStoreSomeDependency = 0;
            //Severe dependency on a single store for growth:
            let checkStoreSevereDependency = 0;

            //Check each store:
            for (let m = 0; m < storeGrowth.length; m++) {
                //Get raw growth value:
                const storeYoYSalesIncrease = storeGrowth[m].rawGrowth;
                //Y calculation:
                y = ((storeYoYSalesIncrease / overallCompanyYoYSales) * 100);

                if (y <= 50) {
                    checkStoreNoDependency = checkStoreNoDependency + 1;
                } else if (y < 75) {
                    checkStoreSomeDependency = checkStoreSomeDependency + 1;
                } else { //y >= 75
                    checkStoreSevereDependency = checkStoreSevereDependency + 1;
                }
            }

            //Red text - sales are declining:
            if (overallCompanyYoYSales < 0) {
                ragStatus = "error";
                ragValue = "Sales are decreasing";
            }
            //Amber text - severe dependency on at least one store:
            else if (checkStoreSevereDependency >= 1) {
                ragStatus = "warning";
                ragValue = "Sales are growing and there's a severe dependency on a singular store for growth";
            }
            //Green text - no dependency on any store:
            else if (checkStoreNoDependency === numberOfStores) {
                ragStatus = "success";
                ragValue = "Sales are growing and there's no dependency on a singular store for growth";
            }
            //Green text -  some dependency on at least one store:
            else { //checkStoreSomeDependency >= 1
                ragStatus = "success";
                ragValue = "Sales are growing and there's some dependency on a singular store for growth";
            }
        } else {
            //check for dependency on singular region

            //Set label value:
            ragLabel = "Dependency on singular region";

            //Check for no dependency on a single store:
            let checkRegionNoDependency = 0;
            //Check for some dependency on a single store:
            let checkRegionSomeDependency = 0;
            //Check for severe dependency on a single store:
            let checkRegionSevereDependency = 0;

            //Check each region:
            for (let n = 0; n < distinctRegionSales.length; n++) {
                //Get raw growth value:
                const regionYoYStoreIncrease = distinctRegionSales[n].regionGrowth;
                //Y calculation:
                y = ((regionYoYStoreIncrease / overallCompanyYoYSales) * 100);

                if (y <= 50) {
                    checkRegionNoDependency = checkRegionNoDependency + 1;
                } else if (y < 75) {
                    checkRegionSomeDependency = checkRegionSomeDependency + 1;
                } else { // y >= 75
                    checkRegionSevereDependency = checkRegionSevereDependency + 1;
                }
            }

            //Red text - sales are declining:
            if (overallCompanyYoYSales < 0) {
                ragStatus = "error";
                ragValue = "Sales are decreasing";
            }
            //Amber text - severe dependency on at least one region:
            else if (checkRegionSevereDependency >= 1) {
                ragStatus = "warning";
                ragValue = "Sales are growing and there's a severe dependency on a singular region for growth";

            }
            //Green text - no dependency on any region:
            else if (checkRegionNoDependency === numberDistinctRegions) {
                ragStatus = "success";
                ragValue = "Sales are growing and there's no dependency on a singular region for growth";

            }
            //Green text - some dependency on at least one region:
            else { //checkRegionSomeDependency >= 1
                ragStatus = "success";
                ragValue = "Sales are growing and there's some dependency on a singular region for growth";
            }
        }

        const label = ragLabel;
        const status = ragStatus;
        const value = ragValue;

        dispatch(actions.getDependencyOnSingularRegionOrStoreSuccess(label, status, value));
    }
    catch (error) {
        dispatch(actions.getDependencyOnSingularRegionOrStoreFailure());
        dispatch(logError("Error loading DependencyOnSingularRegionOrStore.", error));
    }
};

const setSelectedRegion = (selectedRegion) => (dispatch) => {
    dispatch(actions.setSelectedRegion(selectedRegion));
};

const operations = {
    getTotalSalesGrowthByRegion,
    getSalesByRegionGrowth,
    getDependencyOnSingularRegionOrStore,
    setSelectedRegion
};

export default operations;
